import React, { useState } from "react";
import { Datagrid, FunctionField, List, TextField, useNotify, useRefresh, Button } from "react-admin";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { DeleteForever } from "@material-ui/icons";
import { CustomDateField } from "../../Components/CustomDateField";
import { EnumField } from "../../Components/EnumField";
import CreatedByField from "../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../Constants/Constant";
import { DownloadButton } from "@airborne/react-admin";
import { useAppDataProvider } from "../../dataprovider";

export const ExcusableHistory = (props: any) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any>(null);
  const [validationError, setValidationError] = useState<string>("");
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useAppDataProvider();

  const validateHistoryDeletion = async (recordToDelete: any) => {
    try {
      // Get all records to check count
      const result = await dataProvider.getList("requestorderexcusablehistorys", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
        filter: { requestOrderExcusableId__eq: props.requestOrderExcusableId },
      });

      // Check if there's at least 1 history remaining after deletion
      if (result.data.length <= 1) {
        return {
          canDelete: false,
          message: "You cannot delete this document without uploading the second document of the required file type.",
        };
      }

      return { canDelete: true, message: "" };
    } catch (error) {
      return {
        canDelete: false,
        message: "Error validating deletion. Please try again.",
      };
    }
  };

  const handleDeleteClick = async (record: any, allRecords: any[]) => {
    setRecordToDelete(record);
    const validation = await validateHistoryDeletion(record);
    setValidationError(validation.canDelete ? "" : validation.message);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (recordToDelete && !validationError) {
      const recordId = recordToDelete.id;

      if (!recordId || recordId === 0) {
        notify(`Error: Unable to find valid record ID for deletion`, "error", false, true);
        setConfirmDialogOpen(false);
        setRecordToDelete(null);
        setValidationError("");
        return;
      }

      try {
        await dataProvider.delete("requestorderexcusablehistorys", { id: recordId });
        notify(`History Record Deleted`, "success", false, true);
        refresh(); // Refresh the list to update the data
      } catch (error) {
        notify(`Error : ${error}`, "error", false, true);
      } finally {
        setConfirmDialogOpen(false);
        setRecordToDelete(null);
        setValidationError("");
      }
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setRecordToDelete(null);
    setValidationError("");
  };

  return (
    <>
      <List
        resource="requestorderexcusablehistorys"
        basePath="requestorderexcusablehistorys"
        title={"Excusable"}
        filter={{ requestOrderExcusableId__eq: props.requestOrderExcusableId }}
        sort={DefaultCreatedDateSort}
        perPage={DefaultPerPageSize}
        bulkActionButtons={false}
        exporter={false}
        hasCreate={false}
      >
        <Datagrid title="Excusable History List">
          <FunctionField
            label="Action"
            render={(record: any) => (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  // We'll handle validation differently since we can't access all records here
                  handleDeleteClick(record, []);
                }}
                color="primary"
                style={{ color: "#C90C0F" }}
              >
                <DeleteForever fontSize="small" />
              </IconButton>
            )}
          />
          <TextField source="agreementRef" />
          <TextField
            style={{
              width: "100%",
              whiteSpace: "pre-wrap",
              lineBreak: "anywhere",
            }}
            source="description"
            aria-multiline="true"
            component="pre"
          />
          <TextField
            label="Remark"
            style={{
              width: "100%",
              whiteSpace: "pre-wrap",
              lineBreak: "anywhere",
            }}
            source="remark"
            aria-multiline="true"
            component="pre"
          />
          <FunctionField
            style={{ fontSize: 12 }}
            label="Files"
            render={(record: any) => {
              return record?.files?.map?.((file, index) => {
                return (
                  <Box key={index} style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                    <DownloadButton target={`download/${file.objectKey}`} buttonText={file.name} variant="text" />
                  </Box>
                );
              });
            }}
          />
          <CreatedByField label="Created by" link={false} />
          <CustomDateField source="createdDate" locales="en-GB" />
          <EnumField label={"Action"} enumName={"RequestExcusableStatus"} source={"status"} />
        </Datagrid>
      </List>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {validationError ? "Cannot Delete History" : "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {validationError ? validationError : `Are you sure you want to delete this history record?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary" label={validationError ? "OK" : "Cancel"} />
          {!validationError && <Button onClick={handleConfirmDelete} color="primary" label="Yes" />}
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ExcusableHistory;
